// @flow
import * as React from 'react'
import { type RouterHistory } from 'react-router-dom'
import URI from 'urijs'
import { AppContext, type AppContextValue, withRouter } from 'framework'
import * as passwordResetRepo from 'data/password_reset'
import * as tokenRepo from 'data/token'
import * as userRepo from 'data/user'
import type { User } from 'types'
import { NOMATCH_KEY } from 'ui/NoMatch'
import Loader from 'ui/Loader'
import ResetPasswordInstructions from 'ui/ResetPasswordInstructions'
import { FormControl, Form, FormActions, EmailTextInput, SubmitButton, TextInput } from 'ui/Form'
import Link from 'ui/Link'
import Errors from 'ui/Errors'
// eslint-disable-next-line import/extensions
import { directorDashboardHomePath } from 'main/routes'

import './LoginForm.scss'

export type LoginScreen = 'EMAIL_CHECK' | 'RESET_INSTRUCTIONS'

const EMAIL_CHECK: LoginScreen = 'EMAIL_CHECK'
const RESET_INSTRUCTIONS: LoginScreen = 'RESET_INSTRUCTIONS'

const RESET_PASSWORD_ERROR_TEXT =
  'There was a problem with resetting your password.  Please make sure you entered a correct email address and try again.'

type Props = {
  email: string,
  autoCredentialCheck: boolean,
  source: string,
  redirectTo?: string,
  nativeRedirect: boolean,
  onLogin?: User => void,
  onShowSignUpModal?: () => void,
  onChangeScreen?: LoginScreen => void,
}

type DefaultProps = {
  email: $PropertyType<Props, 'email'>,
  autoCredentialCheck: $PropertyType<Props, 'autoCredentialCheck'>,
  nativeRedirect: boolean,
}

type InjectedProps = {
  history: RouterHistory,
}

type InternalProps = Props & InjectedProps

type State = {
  screen: LoginScreen,
  email: string,
  firstName: string,
  lastName: string,
  password: string,
  isRequestInProgress: boolean,
  errors?: string[],
}

// eslint-disable-next-line react/no-multi-comp
class LoginFormContainer extends React.Component<InternalProps, State> {
  static defaultProps = {
    email: '',
    autoCredentialCheck: false,
    nativeRedirect: false,
  }

  static contextType = AppContext
  context: AppContextValue

  constructor(props: InternalProps) {
    super(props)

    this.state = {
      screen: EMAIL_CHECK,
      email: this.props.email,
      firstName: '',
      lastName: '',
      password: '',
      isRequestInProgress: false,
    }
  }

  componentDidMount = () => {
    if (this.props.autoCredentialCheck && this.state.email !== '') {
      this.checkCredentials()
    }
  }

  componentWillReceiveProps = (nextProps: Props) => {
    if (nextProps.email !== this.props.email) {
      this.setState({ email: nextProps.email })
    }
  }

  render = () => {
    if (this.state.isRequestInProgress) return <Loader />

    switch (this.state.screen) {
      case RESET_INSTRUCTIONS:
        return this.renderResetPasswordInstructions()

      default:
        return this.renderEmailPasswordForm()
    }
  }

  renderEmailPasswordForm = () => {
    return (
      <Form onSubmit={this.handleSubmit}>
        <FormControl label="Email">
          <EmailTextInput
            name="email"
            value={this.state.email}
            onChange={this.handleEmailChange}
            autoFocus
            required
          />
        </FormControl>
        <FormControl label="Password">
          <TextInput
            type="password"
            name="password"
            value={this.state.password}
            onChange={this.handlePasswordChange}
            required
          />
        </FormControl>
        <div className="LoginForm-emailFormForgotPassword">
          <Link onClick={this.handleRequestPasswordReset}>
            Click here if you forgot your password or need to set up your password for the first
            time
          </Link>
        </div>

        <FormActions>
          <Errors errors={this.state.errors} />
          <SubmitButton full disabled={this.state.isRequestInProgress}>
            Login
          </SubmitButton>
        </FormActions>
      </Form>
    )
  }

  handleSubmit = () => {
    this.checkCredentials()
    this.handlePasswordLoginFormSubmit()
  }

  renderResetPasswordInstructions = () => {
    return <ResetPasswordInstructions email={this.state.email} />
  }

  handleEmailChange = (email: string) => {
    this.setState({ email })
  }

  handlePasswordChange = (password: string) => {
    this.setState({ password })
  }

  checkCredentials = () => {
    this.setState({ isRequestInProgress: true })

    userRepo
      .credentialCheck(this.state.email)
      .catch(errors => this.setState({ errors }))
      .then(() => this.setState({ isRequestInProgress: false }))
  }

  handleRequestPasswordReset = () => {
    this.setState({ isRequestInProgress: true })

    this.initiatePasswordReset(RESET_INSTRUCTIONS)
      .catch(() => this.setState({ errors: [RESET_PASSWORD_ERROR_TEXT] }))
      .then(() => this.setState({ isRequestInProgress: false }))
  }

  handlePasswordLoginFormSubmit = () => {
    this.setState({ isRequestInProgress: true })

    this.authCredentials()
      .then(this.handleOnAuthenticated)
      .catch(errors => this.setState({ errors, isRequestInProgress: false }))
  }

  authCredentials = () => {
    return tokenRepo.create({ email: this.state.email, password: this.state.password })
  }

  initiatePasswordReset = (successLoginScreen: LoginScreen) => {
    const destination =
      this.props.redirectTo || `${window.location.pathname}${window.location.search}`
    return passwordResetRepo
      .create(this.state.email, destination)
      .then(() => this.setScreen(successLoginScreen))
  }

  handleOnAuthenticated = (user: User) => {
    this.context.setUser(user)
    if (this.props.onLogin) this.props.onLogin(user)
    this.redirectUser(user)
  }

  setScreen = (screen: LoginScreen) => {
    this.setState({ screen })
    if (this.props.onChangeScreen) this.props.onChangeScreen(screen)
  }

  redirectUser = (user: User) => {
    // remove any nomatch param so it can do a reload on the next page if necessary
    const redirectUri = URI(this.props.redirectTo || '/').removeQuery(NOMATCH_KEY)

    const isDirector = !!user.schoolhouse && !user.schoolhouse.closedAt

    if (isDirector && redirectUri.path() === '/') {
      redirectUri.directory(directorDashboardHomePath())
      return window.location.assign(redirectUri.toString())
    }

    const redirectPath = redirectUri.toString()

    return this.props.nativeRedirect
      ? window.location.assign(redirectPath)
      : this.props.history.push(redirectPath)
  }
}

export default (withRouter(LoginFormContainer): React.ComponentType<$Diff<Props, DefaultProps>>)
